import { BaseChartProperty } from '@src/slide/domain/ui-model/element-modeles/property/chart/base-property';
import { AxisName } from '@src/slide/domain/ui-model/element-modeles/property/chart/axis/axis.interface';
import { transaction } from '@src/slide/domain/slide-app';
import forEach from 'lodash/forEach';
import { GridlinesType } from '../../../constant/chart';
import { AxisProperty } from './axis';
import type {
    IValuesRange,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/base-sub-chart.interface';
import type { IAxisProperty } from '@src/slide/domain/ui-model/element-modeles/property/chart/axis/axis.interface';
import type {
    IAxesData,
    IAxesProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/axis/axes.interface';
import type { IChartAxis } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-properties.interface';
import { IBarChart, ISubChartModel } from '../../../Interface/chart-v3/sub-chart';
import { STBarDir, SubChartType } from '../../../Interface/chart-v3/sdk-model.interface';

export class AxesProperty extends BaseChartProperty<IAxesData> implements IAxesProperty {
    public axisList: IAxisProperty[];

    public init() {
        const { categoryAxes = [], valueAxes = [], dateAxes = []  } = this.uiData;
        const axes: IChartAxis[] = [
            ...this.appendAxisTypeAndIndex(categoryAxes, 'category'),
            ...this.appendAxisTypeAndIndex(dateAxes, 'date'),
            ...this.appendAxisTypeAndIndex(valueAxes, 'value'),
        ];

        this.axisList = axes.map(axis => new AxisProperty(axis, this.context).init());

        this.initAxisName();

        return this;
    }

    public getRelativeAxisById(axisId: number): IAxisProperty | undefined {
        return this.axisList.find(axis => axis.axId === axisId);
    }

    /*
     * 坐标轴需要所有的子表数据。会在上层子表初始化完之后统一调度
     */
    public initAxisValues() {
        const subCharts = this.context.getPropertyModule('subCharts');
        if (!subCharts) return;

        // 设置值坐标轴的values，聚合所有使用到该坐标轴的数据
        this.axisList.forEach((axis) => {
            // 值坐标轴进行values计算
            let values: number[] = [];
            let range: undefined | IValuesRange = undefined;
            if (axis.type === 'value') {
                subCharts.forEach((subChart) => {
                    if (!subChart) {
                        return;
                    }

                    // 只要用到了这根轴的，就将该子表的数据加入到对应轴的数据集合中
                    const isChartShareValueAxis = subChart.axisIds?.some(axisId => axisId === axis.axId);
                    if (isChartShareValueAxis) {
                        const { values: dataValues, range: dataRange } = subChart.getAxisValuesFromData(axis);
                        values = [...values, ...dataValues];

                        if (dataRange) {
                            if (!range) {
                                range = dataRange;
                            } else {
                                // 计算出更广的那个range
                                range[0] = Math.min(range[0], dataRange[0]);
                                range[1] = Math.max(range[1], dataRange[1]);
                            }
                        }
                    }
                });
            }

            axis.setValues(values, range);
            // 交给子表做后处理
            subCharts.forEach(subChart => subChart.processAxis(axis));
        });
    }

    /** 主要横坐标 */
    public get primaryHorizontalAxis(): IAxisProperty | undefined {
        return this.axisList.find(axis => axis.name === AxisName.PRIMARY_HORIZONTAL);
    }
    /** 主要纵坐标轴 */
    public get primaryVerticalAxis(): IAxisProperty | undefined {
        return this.axisList.find(axis => axis.name === AxisName.PRIMARY_VERTICAL);
    }
    /** 次要横坐标轴 */
    public get secondaryHorizontalAxis(): IAxisProperty | undefined {
        return this.axisList.find(axis => axis.name === AxisName.SECONDARY_HORIZONTAL);
    }
    /** 次要纵坐标轴 */
    public get secondaryVerticalAxis(): IAxisProperty | undefined {
        return this.axisList.find(axis => axis.name === AxisName.SECONDARY_VERTICAL);
    }

    public getAxisByName(axisName: AxisName): IAxisProperty | undefined {
        return this.axisList.find(axis => axis.name === axisName);
    }

    public getAllAxes(): (IAxisProperty|undefined)[] {
        return [
            this.primaryHorizontalAxis,
            this.primaryVerticalAxis,
            this.secondaryHorizontalAxis,
            this.secondaryVerticalAxis,
        ];
    }

    public showAllAxes() {
        transaction(() => {
            forEach(this.axisList, axis => axis.showAxis());
        });
    }

    public hideAllAxes() {
        transaction(() => {
            forEach(this.axisList, axis => axis.hideAxis());
        });
    }

    public createAllAxesTitle(): void {
        transaction(() => {
            forEach(this.axisList, axis => axis.createAxisTitle());
        });
    }

    public removeAllAxesTitle(): void {
        transaction(() => {
            forEach(this.axisList, axis => axis.removeAxisTitle());
        });
    }

    public createAllAxesGridlines(): void {
        transaction(() => {
            forEach(this.axisList, axis => axis.createGridlines([GridlinesType.MAJOR_GRID, GridlinesType.MINOR_GRID]));
        });
    }

    public removeAllAxesGridlines(): void {
        transaction(() => {
            forEach(this.axisList, axis => axis.removeGridlines([GridlinesType.MAJOR_GRID, GridlinesType.MINOR_GRID]));
        });
    }

    private appendAxisTypeAndIndex(axes, type): IChartAxis[] {
        return axes.map((axis, index) => ({ ...axis, type, index }));
    }

    private initAxisName() {
        const { charts } = this.context.getChartModel().plotArea;
        // 获取（主/次）（横/纵）坐标轴id
        // 图表的axisIds中， 第一个[横坐标轴id，纵坐标轴id]
        // 主/次 坐标轴判定是根据坐标轴数组index [主要坐标轴，次要坐标轴]
        const horizontalAxisIds = [...new Set(charts.map(chart => chart.axisIds?.[0]).filter(i => !!i))];
        const verticalAxisIds = [...new Set(charts.map(chart => chart.axisIds?.[1]).filter(i => !!i))];
        const [primaryHorizontalAxis, secondaryHorizontalAxis] = this.axisList
            .filter(axis => horizontalAxisIds.includes(axis.axId));
        const [primaryVerticalAxis, secondaryVerticalAxis] = this.axisList
            .filter(axis => verticalAxisIds.includes(axis.axId));

        primaryHorizontalAxis?.setAxisName(this.isBarChart(charts[0])
            ? AxisName.PRIMARY_VERTICAL : AxisName.PRIMARY_HORIZONTAL);
        primaryVerticalAxis?.setAxisName(this.isBarChart(charts[0])
            ? AxisName.PRIMARY_HORIZONTAL : AxisName.PRIMARY_VERTICAL);
        secondaryHorizontalAxis?.setAxisName(this.isBarChart(charts[1])
            ? AxisName.SECONDARY_VERTICAL : AxisName.SECONDARY_HORIZONTAL);
        secondaryVerticalAxis?.setAxisName(this.isBarChart(charts[1])
            ?  AxisName.SECONDARY_HORIZONTAL : AxisName.SECONDARY_VERTICAL);
    }

    private isBarChart(chart?: ISubChartModel) {
        return chart?.type === SubChartType.BarChart && (chart as IBarChart).direction === STBarDir.bar;
    }
}
